import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { Button, Modal, TextField } from "../../../../ui/index.ts";
import {
    OffboardingSectionForm,
    type OffboardingPreparedSubmit,
} from "../../components/offboarding-section-form/index.ts";
import styles from "./exit-interview-modal.module.css";

const personalEmailSchema = z
    .string()
    .trim()
    .min(1, "Personal email is required")
    .email("Enter a valid email");

export interface ExitInterviewModalProps {
    open: boolean;
    onClose: () => void;
    offboardingId: number;
    onSuccess: () => void;
}

export function ExitInterviewModal({
    open,
    onClose,
    offboardingId,
    onSuccess,
}: ExitInterviewModalProps) {
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [personalEmail, setPersonalEmail] = useState("");
    const [emailError, setEmailError] = useState<string | undefined>();
    const [emailSubmitting, setEmailSubmitting] = useState(false);
    const completeSubmitRef = useRef<
        OffboardingPreparedSubmit["completeSubmit"] | null
    >(null);

    useEffect(() => {
        if (!open) {
            setEmailModalOpen(false);
            setPersonalEmail("");
            setEmailError(undefined);
            setEmailSubmitting(false);
            completeSubmitRef.current = null;
        }
    }, [open]);

    const handlePrepareSubmit = (payload: OffboardingPreparedSubmit) => {
        completeSubmitRef.current = payload.completeSubmit;
        setEmailModalOpen(true);
    };

    const handleEmailModalClose = () => {
        if (emailSubmitting) return;
        setEmailModalOpen(false);
        setPersonalEmail("");
        setEmailError(undefined);
    };

    const handleEmailSubmit = async () => {
        const parsed = personalEmailSchema.safeParse(personalEmail);
        if (!parsed.success) {
            setEmailError(parsed.error.issues[0]?.message ?? "Enter a valid email");
            return;
        }

        const completeSubmit = completeSubmitRef.current;
        if (!completeSubmit) return;

        setEmailError(undefined);
        setEmailSubmitting(true);
        const result = await completeSubmit({ personalEmail: parsed.data });
        setEmailSubmitting(false);

        if (result === true) {
            setEmailModalOpen(false);
        }
    };

    return (
        <>
            <Modal
                open={open}
                onClose={onClose}
                title="Exit Interview"
                size="lg"
            >
                <OffboardingSectionForm
                    offboardingId={offboardingId}
                    sectionType="EXIT_INTERVIEW"
                    submitLabel="Submit"
                    successMessage="Exit interview submitted successfully."
                    onSubmitSuccess={() => {
                        onSuccess();
                        onClose();
                    }}
                    onPrepareSubmit={handlePrepareSubmit}
                />
            </Modal>

            <Modal
                open={emailModalOpen}
                onClose={handleEmailModalClose}
                title="Personal Email"
                size="md"
                footer={
                    <div className={styles.footer}>
                        <Button
                            variant="light-grey"
                            onClick={handleEmailModalClose}
                            disabled={emailSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="success"
                            onClick={handleEmailSubmit}
                            disabled={emailSubmitting}
                        >
                            {emailSubmitting ? "Submitting..." : "Submit"}
                        </Button>
                    </div>
                }
            >
                <div className={styles.body}>
                    <p className={styles.description}>
                        Please provide your personal email address to complete
                        your exit interview submission.
                    </p>
                    <TextField
                        id="exit-interview-personal-email"
                        label="Personal Email"
                        type="email"
                        required
                        value={personalEmail}
                        onChange={(e) => {
                            setPersonalEmail(e.target.value);
                            if (emailError) setEmailError(undefined);
                        }}
                        error={emailError}
                        placeholder="name@example.com"
                        autoComplete="email"
                    />
                </div>
            </Modal>
        </>
    );
}
